import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";

import { BillingCard } from "../components/profile/BillingCard";
import { CustomVoicesCard } from "../components/profile/CustomVoicesCard";
import { SnippetCard } from "../components/profile/SnippetCard";
import { Tabs, Text, View } from "../components/base";
import { Icon } from "../components/base/Icon";
import { Dialog } from "../components/base/Dialog";
import { InputField } from "../components/base/InputField";
import { organizationActions } from "../redux/reducers/organizationSlice";
import { MembersCard } from "../components/profile/MembersCard";
import { organizationInvitesActions } from "../redux/reducers/organizationInvitesSlice";
import { organizationMembersActions } from "../redux/reducers/organizationMemberSlice";
import { organizationTelephonyActions, OrganizationTelephony } from "../redux/reducers/organizationTelephonySlice";
import { OrganizationVariable, organizationVariablesActions } from "../redux/reducers/organizationVariablesSlice";
import { organizationCustomVoicesActions } from "../redux/reducers/organizationCustomVoicesSlice";
import { organizationSnippetActions, OrganizationSnippet } from "../redux/reducers/organizationSnippet";
import { STRIPE_PUBLISHABLE_KEY } from "../config";
import { TelephonyCard } from "../components/profile/TelephonyCard";
import { PhoneNumbersCard } from "../components/profile/PhoneNumbersCard";
import { organizationPhoneNumberActions } from "../redux/reducers/organizationPhoneNumberSlice";
import { useApiRequest } from "../hooks/useApiRequest";
import { useAppDispatch, useAppSelector } from "../redux";
import { useEffectiveOrganization } from "../hooks/useEffectiveOrganization";
import { GlobalVariablesCard } from "../components/profile/GlobalVariablesCard";
import { PageLayout } from "../components/PageLayout";

const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

const configurationTabs = [
  { id: "global-variables", label: "Global Variables", icon: "list" },
  { id: "telephony-configurations", label: "Telephony Configurations", icon: "phone" },
  { id: "phone-numbers", label: "Phone Numbers", icon: "number" },
  { id: "audio-snippets", label: "Audio Snippets", icon: "snippet" },
  { id: "custom-voices", label: "Custom Voices", icon: "voice-over" },
];

const filterEntities = <T extends { organizationId: string }>(entities: Record<string, T>, organizationId = "") => {
  return Object.values(entities).filter((entity) => entity.organizationId === organizationId || !entity.organizationId);
};

// Derive the active section from the URL pathname
const getActiveSection = (pathname: string): "members" | "configurations" | "usage" => {
  if (pathname.startsWith("/organization/configurations")) return "configurations";
  if (pathname.startsWith("/organization/usage")) return "usage";
  return "members";
};

// Derive configuration sub-tab from the hash
const getActiveConfigurationTab = (hash: string): string => {
  const cleanHash = hash.replace(/^#/, "");
  const validIds = configurationTabs.map((t) => t.id);
  // Legacy hash support
  const legacyMap: Record<string, string> = {
    variables: "global-variables",
    telephony: "telephony-configurations",
    "phone-numbers": "phone-numbers",
    snippets: "audio-snippets",
    "custom-voices": "custom-voices",
  };
  if (validIds.includes(cleanHash)) return cleanHash;
  if (legacyMap[cleanHash]) return legacyMap[cleanHash];
  return "global-variables";
};

// Section titles for the page header
const sectionTitles: Record<string, { title: string; description: string }> = {
  members: { title: "Members", description: "Manage team members and invitations" },
  configurations: { title: "Configurations", description: "Manage global variables, telephony, audio snippets, and custom voices" },
  usage: { title: "Usage", description: "View usage statistics and billing" },
};

export const OrganizationPage = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const user = useAppSelector((state) => state.session.user);

  const [showPurchaseDialog, setShowPurchaseDialog] = useState(false);
  const [amount, setAmount] = useState<number>(10);
  const [isCustomAmount, setIsCustomAmount] = useState(false);
  const [customAmount, setCustomAmount] = useState<string>("");
  const [customAmountError, setCustomAmountError] = useState<string>("");
  const createPaymentRequest = useApiRequest<{ sessionId: string }>();
  const purchasePhoneNumberRequest = useApiRequest();
  const addPhoneNumberRequest = useApiRequest();
  const releasePhoneNumberRequest = useApiRequest();
  const [isPlayingVoiceId, setIsPlayingVoiceId] = useState("");
  const currentPlayingVoice = useRef<HTMLAudioElement | null>(null);
  const { organizationId: effectiveOrganizationId } = useEffectiveOrganization();

  const organizationVariables = useAppSelector((state) => filterEntities(state.organizationVariables.entities, effectiveOrganizationId));
  const organizationMembers = useAppSelector((state) => filterEntities(state.organizationMembers.entities, effectiveOrganizationId));
  const organizationInvites = useAppSelector((state) => filterEntities(state.organizationInvites.entities, effectiveOrganizationId));
  const organizationTelephony = useAppSelector((state) => filterEntities(state.organizationTelephony.entities, effectiveOrganizationId));
  const organizationTelephonyIsLoading = useAppSelector((state) => state.organizationTelephony.isLoading);
  const organizationCustomVoices = useAppSelector((state) => filterEntities(state.organizationCustomVoices.entities, effectiveOrganizationId));
  const organizationCustomVoicesIsLoading = useAppSelector((state) => state.organizationCustomVoices.isLoading);
  const organizationSnippets = useAppSelector((state) => filterEntities(state.organizationSnippets.entities, effectiveOrganizationId));
  const organizationSnippetsIsLoading = useAppSelector((state) => state.organizationSnippets.isLoading);
  const organizationPhoneNumbers = useAppSelector((state) => filterEntities(state.organizationPhoneNumbers.entities, effectiveOrganizationId));
  const organizationPhoneNumbersIsLoading = useAppSelector((state) => state.organizationPhoneNumbers.isLoading);

  // Derive active section from the pathname
  const activeSection = getActiveSection(location.pathname);
  const sectionInfo = sectionTitles[activeSection];

  // Configuration sub-tab from hash
  const [activeConfigurationTab, setActiveConfigurationTab] = useState(() => getActiveConfigurationTab(window.location.hash));

  // Sync configuration sub-tab when hash changes
  useEffect(() => {
    if (activeSection === "configurations") {
      setActiveConfigurationTab(getActiveConfigurationTab(location.hash));
    }
  }, [location.hash, activeSection]);

  // Update hash when configuration sub-tab changes
  useEffect(() => {
    if (activeSection === "configurations") {
      window.location.hash = activeConfigurationTab;
    }
  }, [activeConfigurationTab, activeSection]);

  // Filter tabs based on user role - show billing to system admins and organization admins
  const currentMember = organizationMembers.find((member) => member.userId === user?.id);
  const isOrganizationAdmin = currentMember?.role === "admin" || currentMember?.role === "owner";

  const onClickInviteUser = (email: string, role: "admin" | "member") => {
    if (effectiveOrganizationId) {
      dispatch(
        organizationInvitesActions.create({
          organizationId: effectiveOrganizationId,
          email,
          role,
        }),
      );
    }
  };

  const onClickCancelInvite = (id: string) => {
    if (effectiveOrganizationId) {
      dispatch(
        organizationInvitesActions.deleteById({
          organizationId: effectiveOrganizationId,
          id,
        }),
      );
    }
  };

  const onClickUpdateMemberRole = (id: string, role: "admin" | "member") => {
    if (effectiveOrganizationId) {
      dispatch(
        organizationMembersActions.updateById({
          organizationId: effectiveOrganizationId,
          id,
          role,
        }),
      );
    }
  };

  const onClickRemoveMember = (memberId: string) => {
    if (effectiveOrganizationId) {
      dispatch(
        organizationMembersActions.deleteById({
          organizationId: effectiveOrganizationId,
          id: memberId,
        }),
      );
    }
  };

  const onClickPurchaseAmount = async () => {
    try {
      const stripe = await stripePromise;
      if (!stripe) throw new Error("Stripe failed to load");

      const response = await createPaymentRequest.fetch("/v1/payments/create-session", {
        method: "POST",
        body: { amount },
      });

      const result = await stripe.redirectToCheckout({
        sessionId: response.data?.sessionId || "",
      });

      if (result.error) {
        console.error(result.error);
      }
    } catch (err) {
      console.error("Payment error:", err);
    }
  };

  // Manage voice audio event listeners
  useEffect(() => {
    if (!currentPlayingVoice.current || !isPlayingVoiceId) return;

    const audio = currentPlayingVoice.current;
    const handleEnded = () => setIsPlayingVoiceId("");
    const handlePause = () => setIsPlayingVoiceId("");
    const handleError = () => {
      setIsPlayingVoiceId("");
      currentPlayingVoice.current = null;
    };

    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("error", handleError);

    return () => {
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("error", handleError);
    };
  }, [isPlayingVoiceId]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (currentPlayingVoice.current) {
        currentPlayingVoice.current.pause();
        currentPlayingVoice.current = null;
        setIsPlayingVoiceId("");
      }
    };
  }, []);

  const onChangeOrganizationVariables = (updatedVariable: Partial<OrganizationVariable>) => {
    dispatch(
      organizationVariablesActions.updateById({
        ...updatedVariable,
        organizationId: effectiveOrganizationId,
      }),
    );
  };

  const onClickAddOrganizationVariable = () => {
    dispatch(
      organizationVariablesActions.create({
        name: "",
        value: "",
        masked: false,
        organizationId: effectiveOrganizationId,
      }),
    );
  };

  const onClickDeleteOrganizationVariable = async (id: string) => {
    await dispatch(
      organizationVariablesActions.deleteById({
        id,
        organizationId: effectiveOrganizationId,
      }),
    );
  };

  const onClickAddTelephony = (config: Partial<OrganizationTelephony>) => {
    if (effectiveOrganizationId) {
      dispatch(
        organizationTelephonyActions.create({
          ...config,
          organizationId: effectiveOrganizationId,
        }),
      );
    }
  };

  const onClickUpdateTelephony = (config: Partial<OrganizationTelephony>) => {
    if (effectiveOrganizationId) {
      dispatch(
        organizationTelephonyActions.updateById({
          ...config,
          organizationId: effectiveOrganizationId,
        }),
      );
    }
  };

  const onClickDeleteTelephony = (id: string) => {
    if (effectiveOrganizationId) {
      dispatch(
        organizationTelephonyActions.deleteById({
          id,
          organizationId: effectiveOrganizationId,
        }),
      );
    }
  };

  // Phone number handlers
  const onClickPurchasePhoneNumber = async (data: { telephonyId: string; areaCode: string; countryCode: string; friendlyName: string }) => {
    if (effectiveOrganizationId) {
      await purchasePhoneNumberRequest.fetch(`/v1/organizations/${effectiveOrganizationId}/phoneNumbers/purchase`, {
        method: "POST",
        body: data,
      });
      dispatch(organizationPhoneNumberActions.get({ organizationId: effectiveOrganizationId }));
    }
  };

  const onClickAddPhoneNumber = async (data: { telephonyId: string; phoneNumber: string; friendlyName: string }) => {
    if (effectiveOrganizationId) {
      await addPhoneNumberRequest.fetch(`/v1/organizations/${effectiveOrganizationId}/phoneNumbers`, {
        method: "POST",
        body: data,
      });
      dispatch(organizationPhoneNumberActions.get({ organizationId: effectiveOrganizationId }));
    }
  };

  const onClickUpdatePhoneNumber = (data: Partial<{ id: string; friendlyName: string; organizationId: string }>) => {
    if (effectiveOrganizationId) {
      dispatch(
        organizationPhoneNumberActions.updateById({
          ...data,
          organizationId: effectiveOrganizationId,
        }),
      );
    }
  };

  const onClickDeletePhoneNumber = (id: string) => {
    if (effectiveOrganizationId) {
      dispatch(
        organizationPhoneNumberActions.deleteById({
          id,
          organizationId: effectiveOrganizationId,
        }),
      );
    }
  };

  const onClickReleasePhoneNumber = async (id: string) => {
    if (effectiveOrganizationId) {
      await releasePhoneNumberRequest.fetch(`/v1/organizations/${effectiveOrganizationId}/phoneNumbers/${id}/release`, {
        method: "POST",
      });
      dispatch(organizationPhoneNumberActions.get({ organizationId: effectiveOrganizationId }));
    }
  };

  // Custom voices handlers
  const handleCreateVoice = async (params: { name: string; description: string; language: string; file: File }) => {
    if (!effectiveOrganizationId) return;

    await dispatch(
      organizationCustomVoicesActions.createVoiceClone({
        organizationId: effectiveOrganizationId,
        name: params.name,
        description: params.description,
        language: params.language,
        file: params.file,
      }),
    );
  };

  const handleUpdateVoice = async (id: string, params: { name: string; description: string }) => {
    if (!effectiveOrganizationId) return;

    await dispatch(
      organizationCustomVoicesActions.updateById({
        id,
        organizationId: effectiveOrganizationId,
        name: params.name,
        description: params.description,
      }),
    );
  };

  const handleDeleteVoice = async (voiceId: string) => {
    if (!effectiveOrganizationId) return;

    await dispatch(
      organizationCustomVoicesActions.deleteById({
        id: voiceId,
        organizationId: effectiveOrganizationId,
      }),
    );
  };

  // Audio snippet handlers
  const handleCreateSnippet = (snippet: OrganizationSnippet) => {
    // Snippet is already created via the dialog, so we just need to refresh
    if (effectiveOrganizationId) {
      dispatch(organizationSnippetActions.get({ organizationId: effectiveOrganizationId }));
    }
  };

  const handleUpdateSnippet = async (id: string, params: { name: string }) => {
    if (!effectiveOrganizationId) return;

    await dispatch(
      organizationSnippetActions.updateById({
        id,
        organizationId: effectiveOrganizationId,
        name: params.name,
      }),
    );
  };

  const handleDeleteSnippet = async (id: string) => {
    if (!effectiveOrganizationId) return;

    await dispatch(
      organizationSnippetActions.deleteById({
        id,
        organizationId: effectiveOrganizationId,
      }),
    );
  };

  useEffect(() => {
    if (effectiveOrganizationId) {
      dispatch(organizationActions.getById({ id: effectiveOrganizationId }));
      dispatch(organizationVariablesActions.get({ organizationId: effectiveOrganizationId }));
      dispatch(organizationMembersActions.get({ organizationId: effectiveOrganizationId }));
      dispatch(organizationInvitesActions.get({ organizationId: effectiveOrganizationId, status: "pending" }));
      dispatch(organizationTelephonyActions.get({ organizationId: effectiveOrganizationId }));
      dispatch(organizationCustomVoicesActions.get({ organizationId: effectiveOrganizationId }));
      dispatch(organizationSnippetActions.get({ organizationId: effectiveOrganizationId }));
      dispatch(organizationPhoneNumberActions.get({ organizationId: effectiveOrganizationId }));
    }
  }, [effectiveOrganizationId]);

  const renderTabContent = () => {
    switch (activeSection) {
      case "members":
        return (
          <View className="flex flex-col gap-6">
            <MembersCard
              members={organizationMembers}
              invites={organizationInvites.filter((invite) => invite.status === "pending")}
              onInviteUser={onClickInviteUser}
              onCancelInvite={onClickCancelInvite}
              onUpdateMemberRole={onClickUpdateMemberRole}
              onRemoveMember={onClickRemoveMember}
              maxVisible={20}
            />
          </View>
        );
      case "configurations":
        return (
          <View className="flex flex-col gap-6">
            <View className="border-b border-gray-200 dark:border-gray-700 -mx-1">
              <Tabs
                tabs={configurationTabs}
                activeTab={activeConfigurationTab}
                onChange={setActiveConfigurationTab}
              />
            </View>

            {activeConfigurationTab === "global-variables" && (
              <GlobalVariablesCard
                variables={organizationVariables}
                onChange={onChangeOrganizationVariables}
                onClickAdd={onClickAddOrganizationVariable}
                onClickDelete={onClickDeleteOrganizationVariable}
              />
            )}

            {activeConfigurationTab === "telephony-configurations" && (
              <TelephonyCard
                telephonyConfigs={organizationTelephony}
                onAdd={onClickAddTelephony}
                onUpdate={onClickUpdateTelephony}
                onDelete={onClickDeleteTelephony}
                isLoading={organizationTelephonyIsLoading}
              />
            )}

            {activeConfigurationTab === "phone-numbers" && (
              <PhoneNumbersCard
                phoneNumbers={organizationPhoneNumbers}
                onPurchase={onClickPurchasePhoneNumber}
                onAdd={onClickAddPhoneNumber}
                onUpdate={onClickUpdatePhoneNumber}
                onDelete={onClickDeletePhoneNumber}
                onRelease={onClickReleasePhoneNumber}
                isPurchasing={purchasePhoneNumberRequest.isLoading}
                isAdding={addPhoneNumberRequest.isLoading}
                isDeleting={organizationPhoneNumbersIsLoading}
                isReleasing={releasePhoneNumberRequest.isLoading}
                isLoading={organizationPhoneNumbersIsLoading}
                organizationId={effectiveOrganizationId}
              />
            )}

            {activeConfigurationTab === "custom-voices" && (
              <CustomVoicesCard
                customVoices={organizationCustomVoices}
                isLoading={organizationCustomVoicesIsLoading}
                onCreateVoice={handleCreateVoice}
                onUpdateVoice={handleUpdateVoice}
                onDeleteVoice={handleDeleteVoice}
              />
            )}

            {activeConfigurationTab === "audio-snippets" && (
              <SnippetCard
                snippets={organizationSnippets}
                isLoading={organizationSnippetsIsLoading}
                organizationId={effectiveOrganizationId || ""}
                onCreateSnippet={handleCreateSnippet}
                onUpdateSnippet={handleUpdateSnippet}
                onDeleteSnippet={handleDeleteSnippet}
              />
            )}
          </View>
        );
      case "usage":
        return <BillingCard />;
      default:
        return null;
    }
  };

  return (
    <PageLayout
      title={sectionInfo.title}
      description={sectionInfo.description}
    >
      <View className="mt-6">{renderTabContent()}</View>

      <Dialog
        isOpen={showPurchaseDialog}
        onClose={() => setShowPurchaseDialog(false)}
        title="Add Funds"
      >
        <View className="p-4 space-y-4">
          <Text className="text-sm font-medium text-gray-700 dark:text-gray-300">Select Amount:</Text>
          <View className="grid grid-cols-2 gap-2">
            {[5, 10, 25, 50].map((amt) => (
              <button
                key={amt}
                onClick={() => {
                  setAmount(amt);
                  setIsCustomAmount(false);
                  setCustomAmount("");
                }}
                className={`w-full px-3.5 py-2 rounded-lg text-sm font-medium transition-colors border ${
                  amount === amt && !isCustomAmount
                    ? "bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800"
                    : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
                }`}
              >
                ${amt}
              </button>
            ))}
          </View>

          <button
            onClick={() => {
              setIsCustomAmount(true);
              setAmount(0);
            }}
            className={`w-full px-3.5 py-2 rounded-lg text-sm font-medium transition-colors border ${
              isCustomAmount
                ? "bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800"
                : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
            }`}
          >
            Enter Custom Amount
          </button>

          {isCustomAmount && (
            <View className="space-y-2">
              <Text className="text-sm text-gray-600 dark:text-gray-400">Enter custom amount (USD):</Text>
              <View className="relative">
                <Text className="absolute left-3 top-1/2 -translate-y-1/2">$</Text>
                <InputField
                  type="number"
                  value={customAmount}
                  onChange={(value) => {
                    setCustomAmount(value);
                    const numValue = Number(value) || 0;
                    if (numValue < 5 && value !== "") {
                      setCustomAmountError("Minimum amount is $5");
                      setAmount(0);
                    } else {
                      setCustomAmountError("");
                      setAmount(numValue);
                    }
                  }}
                  className={`w-full pl-7 ${customAmountError ? "border-red-500 dark:border-red-400" : ""}`}
                  placeholder="0.00"
                />
              </View>
              {customAmountError && <Text className="text-red-500 dark:text-red-400 text-sm">{customAmountError}</Text>}
            </View>
          )}

          <button
            onClick={onClickPurchaseAmount}
            disabled={amount < 5 || createPaymentRequest.isLoading}
            className="flex flex-row items-center justify-center gap-1.5 w-full px-3.5 py-2 rounded-lg text-sm font-medium transition-colors border bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800 hover:bg-emerald-100 dark:hover:bg-emerald-900/50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {createPaymentRequest.isLoading ? (
              <Icon
                name="loader"
                size={14}
                className="animate-spin"
              />
            ) : (
              <Icon
                name="add"
                size={14}
              />
            )}
            {createPaymentRequest.isLoading ? "Processing..." : `Add $${amount === 0 ? "0.00" : amount.toFixed(2)}`}
          </button>
        </View>
      </Dialog>
    </PageLayout>
  );
};
